import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore, auth } from "../../../firebase"; // Cleaned up import
import {
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box, // Added
  Container, // Added
  Chip, // Added
  CircularProgress, // Added
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  InputLabel,
  TextField,
  Rating,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import {
  COMPLAINT_STATUS_OPTIONS,
  getComplaintStatusColor,
  normalizeComplaintStatus,
} from "../../../utils/complaintStatus";

function ComplaintsList() {
  const [complaints, setComplaints] = useState([]);
  const [itemDetails, setItemDetails] = useState({});
  const [draftStatuses, setDraftStatuses] = useState({});
  const [facilitySellers, setFacilitySellers] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignComplaintId, setAssignComplaintId] = useState(null);
  const [selectedAssignSellerId, setSelectedAssignSellerId] = useState("");
  const [assigningComplaint, setAssigningComplaint] = useState(false);
  const [loading, setLoading] = useState(true);
  const [complaintFilter, setComplaintFilter] = useState("active");
  const [closeRequestDialogOpen, setCloseRequestDialogOpen] = useState(false);
  const [closeRequestComplaintId, setCloseRequestComplaintId] = useState(null);
  const [denyCloseRequestDialogOpen, setDenyCloseRequestDialogOpen] = useState(false);
  const [denyCloseRequestComplaintId, setDenyCloseRequestComplaintId] = useState(null);
  const [denyCloseRequestReason, setDenyCloseRequestReason] = useState("");
  const [denyCloseRequestError, setDenyCloseRequestError] = useState("");
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonDialogText, setReasonDialogText] = useState("");
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpComplaintId, setHelpComplaintId] = useState(null);
  const [helpMessage, setHelpMessage] = useState("");
  const [helpError, setHelpError] = useState("");
  const [sendingHelp, setSendingHelp] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackComplaintId, setFeedbackComplaintId] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackRemarks, setFeedbackRemarks] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  const userRole = sessionStorage.getItem("userRole") || "";
  const tenantId = sessionStorage.getItem("tenantId") || "";

  const organisationAdminRoles = [
    "organisation_admin",
    "OU Admin",
    "Admin",
  ];
  const organisationOperatorRoles = [
    "organisation_operator",
    "OU User",
    "OU Operator",
    "Operator",
    "User",
  ];
  const isOrganisationAdmin = organisationAdminRoles.includes(userRole);
  const isOrganisationOperator = organisationOperatorRoles.includes(userRole);
  const isFacilityAdmin = userRole === "facility_admin";
  const normalizedUserRole = String(userRole || "").trim().toLowerCase();
  const isSeller = ["seller", "facility_operator"].includes(normalizedUserRole);
  const isCustomer = String(userRole || "").trim().toLowerCase() === "customer";
  const isTenantAdmin = ["admin", "organisation_admin", "OU Admin", "superadmin"].includes(userRole);

  const normalizeEmail = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchComplaints = async () => {
      try {
        if (!currentUser) {
          setLoading(false); // Stop loading if no user
          return;
        }

        const complaintsCollection = collection(firestore, "Complaints");
        const complaintsSnapshot = await getDocs(query(complaintsCollection));

        const allComplaints = complaintsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          status: normalizeComplaintStatus(doc.data().status),
        }));

        let complaintsList = allComplaints;

        if (isOrganisationAdmin) {
          complaintsList = allComplaints.filter(
            (complaint) => complaint.isOrganisationComplaint === true && complaint.tenantId === tenantId
          );
        } else if (isOrganisationOperator) {
          complaintsList = allComplaints.filter(
            (complaint) =>
              complaint.isOrganisationComplaint === true &&
              complaint.tenantId === tenantId &&
              complaint.userId === currentUser.uid
          );
        } else if (isFacilityAdmin) {
          const profileSnapshot = await getDocs(
            query(
              collection(firestore, "UserProfileMaster"),
              where("email", "==", normalizeEmail(currentUser.email))
            )
          );

          const profileDoc = profileSnapshot.empty ? null : profileSnapshot.docs[0];
          const profileData = profileDoc?.data() || {};
          const facilityName = profileData.facilityName || "";
          const branchName = profileData.branchName || "";

          if (profileDoc) {
            const allUsersSnapshot = await getDocs(
              query(collection(firestore, "UserProfileMaster"))
            );

            const sellerRoles = new Set([
              "seller",
              "facility_operator",
              "operator",
              "shop",
            ]);

            const scopedSellers = allUsersSnapshot.docs
              .map((entry) => ({ id: entry.id, ...entry.data() }))
              .filter((entry) => {
                const normalizedRole = String(entry.role || "")
                  .trim()
                  .toLowerCase();
                return (
                  entry.facilityId === profileDoc.id &&
                  sellerRoles.has(normalizedRole)
                );
              })
              .map((entry) => ({
                id: entry.id,
                email: normalizeEmail(entry.email),
                displayName: entry.adminName || entry.displayName || entry.email || "Facility Operator",
              }))
              .filter((entry) => Boolean(entry.email));

            setFacilitySellers(scopedSellers);
          } else {
            setFacilitySellers([]);
          }

          const itemsSnapshot = await getDocs(collection(firestore, "ItemMaster"));
          const facilityItemIds = new Set(
            itemsSnapshot.docs
              .filter((itemDoc) => {
                const itemData = itemDoc.data();
                return (
                  itemData.facilityName === facilityName &&
                  itemData.branchName === branchName
                );
              })
              .map((itemDoc) => itemDoc.id)
          );

          complaintsList = allComplaints.filter((complaint) => {
            if (complaint.isOrganisationComplaint === true) {
              return false;
            }
            return complaint.itemId && facilityItemIds.has(complaint.itemId);
          });
        } else if (isSeller) {
          const currentSellerEmail = normalizeEmail(currentUser.email);
          complaintsList = allComplaints.filter(
            (complaint) =>
              normalizeEmail(complaint.assignedSellerEmail) === currentSellerEmail
          );
        } else if (userRole === "admin") {
          complaintsList = allComplaints.filter(
            (complaint) =>
              complaint.tenantId === tenantId &&
              complaint.isOrganisationComplaint !== true
          );
        } else if (userRole === "superadmin") {
          complaintsList = allComplaints.filter((complaint) => complaint.isOrganisationComplaint !== true);
        } else {
          complaintsList = allComplaints.filter((complaint) => complaint.userId === currentUser.uid);
        }

        setComplaints(complaintsList);

        if (complaintsList.length > 0) {
          const itemDetailsPromises = complaintsList.map((complaint) =>
            getDoc(doc(firestore, "ItemMaster", complaint.itemId)).then(
              (itemDoc) => ({
                itemId: complaint.itemId,
                details: itemDoc.exists() ? itemDoc.data() : {},
              })
            )
          );

          const itemDetailsList = await Promise.all(itemDetailsPromises);
          const itemDetailsMap = itemDetailsList.reduce((acc, itemDetail) => {
            acc[itemDetail.itemId] = itemDetail.details;
            return acc;
          }, {});
          setItemDetails(itemDetailsMap);
        }
      } catch (err) {
        console.error("Error fetching complaints:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchComplaints();
  }, [currentUser, isCustomer, isFacilityAdmin, isOrganisationAdmin, isOrganisationOperator, isSeller, tenantId, userRole]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this complaint?")) {
      try {
        await deleteDoc(doc(firestore, "Complaints", id));
        setComplaints(complaints.filter((complaint) => complaint.id !== id));
      } catch (err) {
        console.error("Error deleting complaint:", err);
      }
    }
  };

  const handleOpenHelpDialog = (complaintId) => {
    setHelpComplaintId(complaintId);
    setHelpMessage("");
    setHelpError("");
    setHelpDialogOpen(true);
  };

  const handleCloseHelpDialog = () => {
    if (sendingHelp) {
      return;
    }
    setHelpDialogOpen(false);
    setHelpComplaintId(null);
    setHelpMessage("");
    setHelpError("");
  };

  const sendHelpEmailToFacilityAdmin = async ({
    recipientEmail,
    complaintId,
    customerEmail,
    customerMessage,
  }) => {
    const apiKey = "qwertyuiop";
    const payload = {
      email: recipientEmail,
      subject: `Customer Help Request - Complaint ${complaintId}`,
      textbody: `Complaint ID: ${complaintId}\nCustomer: ${customerEmail || "N/A"}\n\nMessage:\n${customerMessage}`,
      htmlbody: `<div style="font-family: Arial, sans-serif; color: #333;">
        <h2 style="color: #1976d2;">Customer Help Request</h2>
        <p><strong>Complaint ID:</strong> ${complaintId}</p>
        <p><strong>Customer:</strong> ${customerEmail || "N/A"}</p>
        <p><strong>Message:</strong></p>
        <p>${customerMessage.replace(/\n/g, "<br/>")}</p>
      </div>`,
      apiKey,
    };

    const formData = new FormData();
    formData.append("jsonPayload", JSON.stringify(payload));

    const response = await fetch("https://email-a306.onrender.com/send-email", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to send help report email");
    }
  };

  const handleSendHelpReport = async () => {
    if (!helpComplaintId) {
      return;
    }

    const trimmedMessage = helpMessage.trim();
    if (!trimmedMessage) {
      setHelpError("Please write your issue before sending.");
      return;
    }

    const selectedComplaint = complaints.find((entry) => entry.id === helpComplaintId);
    if (!selectedComplaint) {
      setHelpError("Complaint not found.");
      return;
    }

    const relatedItem = itemDetails[selectedComplaint.itemId] || {};
    const facilityAdminEmail = normalizeEmail(
      selectedComplaint.facilityEmail || relatedItem.facilityEmail
    );

    if (!facilityAdminEmail) {
      setHelpError("Facility admin email not available for this complaint.");
      return;
    }

    try {
      setSendingHelp(true);
      setHelpError("");

      await sendHelpEmailToFacilityAdmin({
        recipientEmail: facilityAdminEmail,
        complaintId: selectedComplaint.id,
        customerEmail: currentUser?.email || selectedComplaint.userEmail || "",
        customerMessage: trimmedMessage,
      });

      handleCloseHelpDialog();
    } catch (error) {
      console.error("Error sending help report:", error);
      setHelpError("Failed to send report. Please try again.");
    } finally {
      setSendingHelp(false);
    }
  };

  const openAssignDialog = (complaintId, currentSellerId = "") => {
    setAssignComplaintId(complaintId);
    setSelectedAssignSellerId(currentSellerId);
    setAssignDialogOpen(true);
  };

  const closeAssignDialog = (force = false) => {
    if (assigningComplaint && !force) {
      return;
    }
    setAssignDialogOpen(false);
    setAssignComplaintId(null);
    setSelectedAssignSellerId("");
  };

  const handleAssignComplaint = async () => {
    if (!isFacilityAdmin || !assignComplaintId || !selectedAssignSellerId) {
      return;
    }

    const selectedSeller = facilitySellers.find(
      (seller) => seller.id === selectedAssignSellerId
    );

    if (!selectedSeller) {
      return;
    }

    setAssigningComplaint(true);
    try {
      const assignPayload = {
        assignedSellerId: selectedSeller.id,
        assignedSellerEmail: selectedSeller.email,
        assignedSellerName: selectedSeller.displayName,
        assignedByFacilityAdmin: currentUser?.email || currentUser?.uid || null,
        assignedAt: new Date(),
        sellerEmail: selectedSeller.email,
        shopEmail: selectedSeller.email,
      };

      await updateDoc(doc(firestore, "Complaints", assignComplaintId), assignPayload);

      setComplaints((prevComplaints) =>
        prevComplaints.map((complaint) =>
          complaint.id === assignComplaintId
            ? { ...complaint, ...assignPayload }
            : complaint
        )
      );

      closeAssignDialog(true);
    } catch (err) {
      console.error("Error assigning complaint to seller:", err);
    } finally {
      setAssigningComplaint(false);
    }
  };

  const canEditStatus = (complaint) => {
    if (complaint?.closeRequestApprovedAt || complaint?.closeRequestApprovedBy || complaint?.closeRequestApproved === true) {
      return false;
    }

    if (complaint.isOrganisationComplaint === true) {
      return isOrganisationAdmin;
    }
    if (isSeller) {
      const currentSellerEmail = normalizeEmail(currentUser?.email);
      return normalizeEmail(complaint.assignedSellerEmail) === currentSellerEmail;
    }
    return ["admin", "facility_admin", "superadmin"].includes(userRole);
  };

  const canDeleteComplaint = () => {
    return ["admin", "facility_admin", "superadmin", "organisation_admin", "OU Admin"].includes(userRole);
  };

  const handleOpenFeedbackDialog = (complaintId) => {
    const selectedComplaint = complaints.find((entry) => entry.id === complaintId);
    if (Number(selectedComplaint?.feedbackRating || 0) > 0) {
      return;
    }
    setFeedbackComplaintId(complaintId);
    setFeedbackRating(Number(selectedComplaint?.feedbackRating || 0));
    setFeedbackRemarks(selectedComplaint?.feedbackRemarks || "");
    setFeedbackError("");
    setFeedbackDialogOpen(true);
  };

  const handleCloseFeedbackDialog = () => {
    if (savingFeedback) {
      return;
    }
    setFeedbackDialogOpen(false);
    setFeedbackComplaintId(null);
    setFeedbackRating(0);
    setFeedbackRemarks("");
    setFeedbackError("");
  };

  const handleSaveFeedback = async () => {
    if (!feedbackComplaintId) {
      return;
    }

    const selectedComplaint = complaints.find((entry) => entry.id === feedbackComplaintId);
    if (Number(selectedComplaint?.feedbackRating || 0) > 0) {
      setFeedbackError("Feedback already submitted and cannot be edited.");
      return;
    }

    if (!feedbackRating || feedbackRating < 1) {
      setFeedbackError("Please select a star rating.");
      return;
    }

    try {
      setSavingFeedback(true);
      setFeedbackError("");

      const feedbackPayload = {
        feedbackRating: Number(feedbackRating),
        feedbackRemarks: feedbackRemarks.trim(),
        feedbackBy: currentUser?.email || currentUser?.uid || null,
        feedbackAt: new Date(),
      };

      await updateDoc(doc(firestore, "Complaints", feedbackComplaintId), feedbackPayload);

      setComplaints((prevComplaints) =>
        prevComplaints.map((complaint) =>
          complaint.id === feedbackComplaintId
            ? { ...complaint, ...feedbackPayload }
            : complaint
        )
      );

      handleCloseFeedbackDialog();
    } catch (error) {
      console.error("Error saving feedback:", error);
      setFeedbackError("Failed to submit feedback. Please try again.");
    } finally {
      setSavingFeedback(false);
    }
  };

  const canMoveStatus = (complaint, requestedStatus) => {
    const currentStatus = normalizeComplaintStatus(complaint?.status);
    const nextStatus = normalizeComplaintStatus(requestedStatus);

    if (!isSeller) {
      return true;
    }

    // Sellers cannot revert In Progress complaints back to Open.
    if (currentStatus === "In Progress" && nextStatus === "Open") {
      return false;
    }

    return true;
  };

  const getStatusOptionsForComplaint = (complaint) => {
    const currentStatus = normalizeComplaintStatus(complaint?.status);

    if (isSeller && currentStatus === "In Progress") {
      return COMPLAINT_STATUS_OPTIONS.filter((status) => normalizeComplaintStatus(status) !== "Open");
    }

    return COMPLAINT_STATUS_OPTIONS;
  };

  const handleStatusSelection = (complaintId, newStatus) => {
    const normalizedStatus = normalizeComplaintStatus(newStatus);

    setDraftStatuses((prev) => {
      const complaint = complaints.find((entry) => entry.id === complaintId);
      if (!complaint) {
        return prev;
      }

      const currentStatus = normalizeComplaintStatus(complaint.status);

      if (normalizedStatus === currentStatus) {
        const { [complaintId]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [complaintId]: normalizedStatus,
      };
    });
  };

  const handleSaveStatus = async (complaintId) => {
    const complaint = complaints.find((entry) => entry.id === complaintId);
    const newStatus = draftStatuses[complaintId];
    if (!complaint || !canEditStatus(complaint)) {
      return;
    }

    if (!newStatus) {
      return;
    }

    if (complaint.closeRequestApprovedAt || complaint.closeRequestApprovedBy || complaint.closeRequestApproved === true) {
      return;
    }

    // Pending close requests can only be completed through Facility Admin approval action.
    if (complaint.closeRequestPending === true) {
      return;
    }

    if (!canMoveStatus(complaint, newStatus) && !(isFacilityAdmin || isTenantAdmin)) {
      return;
    }

    const currentStatus = normalizeComplaintStatus(complaint.status);
    const normalizedStatus = normalizeComplaintStatus(newStatus);

    // Seller cannot directly resolve In Progress complaints; must raise close request.
    if (isSeller && currentStatus === "In Progress" && normalizedStatus === "Resolved") {
      setCloseRequestComplaintId(complaintId);
      setCloseRequestDialogOpen(true);
      return;
    }

    try {
      await updateDoc(doc(firestore, "Complaints", complaintId), {
        status: normalizedStatus,
      });
      // Update local state
      setComplaints(
        complaints.map((complaint) =>
          complaint.id === complaintId
            ? { ...complaint, status: normalizedStatus }
            : complaint
        )
      );
      setDraftStatuses((prev) => {
        const { [complaintId]: _, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      console.error("Error updating complaint status:", err);
    }
  };

  const handleCloseRequestCancel = () => {
    setCloseRequestDialogOpen(false);
    setCloseRequestComplaintId(null);
  };

  const openCloseRequestDialog = (complaintId) => {
    if (!complaintId) {
      return;
    }
    setCloseRequestComplaintId(complaintId);
    setCloseRequestDialogOpen(true);
  };

  const handleRaiseCloseRequest = async () => {
    if (!closeRequestComplaintId) {
      return;
    }

    try {
      await updateDoc(doc(firestore, "Complaints", closeRequestComplaintId), {
        closeRequestPending: true,
        closeRequestRequestedBy: currentUser?.email || currentUser?.uid || null,
        closeRequestRequestedAt: new Date(),
        closeRequestDenied: false,
        closeRequestDeniedReason: "",
        closeRequestDeniedBy: null,
        closeRequestDeniedAt: null,
      });

      setComplaints((prevComplaints) =>
        prevComplaints.map((complaint) =>
          complaint.id === closeRequestComplaintId
            ? {
                ...complaint,
                closeRequestPending: true,
                closeRequestRequestedBy: currentUser?.email || currentUser?.uid || null,
                closeRequestRequestedAt: new Date(),
                closeRequestDenied: false,
                closeRequestDeniedReason: "",
                closeRequestDeniedBy: null,
                closeRequestDeniedAt: null,
              }
            : complaint
        )
      );
      setDraftStatuses((prev) => {
        const { [closeRequestComplaintId]: _, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      console.error("Error raising close request:", err);
    } finally {
      handleCloseRequestCancel();
    }
  };

  const handleApproveCloseRequest = async (complaintId) => {
    if (!isFacilityAdmin) {
      return;
    }

    try {
      await updateDoc(doc(firestore, "Complaints", complaintId), {
        status: "Resolved",
        closeRequestPending: false,
        closeRequestApproved: true,
        closeRequestApprovedBy: currentUser?.email || currentUser?.uid || null,
        closeRequestApprovedAt: new Date(),
        closeRequestDenied: false,
        closeRequestDeniedReason: "",
        closeRequestDeniedBy: null,
        closeRequestDeniedAt: null,
      });

      setComplaints((prevComplaints) =>
        prevComplaints.map((complaint) =>
          complaint.id === complaintId
            ? {
                ...complaint,
                status: "Resolved",
                closeRequestPending: false,
                closeRequestApproved: true,
                closeRequestDenied: false,
                closeRequestDeniedReason: "",
              }
            : complaint
        )
      );
    } catch (err) {
      console.error("Error approving close request:", err);
    }
  };

  const handleOpenDenyCloseRequestDialog = (complaintId) => {
    if (!isFacilityAdmin) {
      return;
    }
    setDenyCloseRequestComplaintId(complaintId);
    setDenyCloseRequestReason("");
    setDenyCloseRequestError("");
    setDenyCloseRequestDialogOpen(true);
  };

  const handleCloseDenyCloseRequestDialog = () => {
    setDenyCloseRequestDialogOpen(false);
    setDenyCloseRequestComplaintId(null);
    setDenyCloseRequestReason("");
    setDenyCloseRequestError("");
  };

  const handleDenyCloseRequest = async () => {
    if (!isFacilityAdmin || !denyCloseRequestComplaintId) {
      return;
    }

    const trimmedReason = denyCloseRequestReason.trim();
    if (!trimmedReason) {
      setDenyCloseRequestError("Please provide a reason for denial.");
      return;
    }

    try {
      await updateDoc(doc(firestore, "Complaints", denyCloseRequestComplaintId), {
        closeRequestPending: false,
        closeRequestDenied: true,
        closeRequestDeniedReason: trimmedReason,
        closeRequestDeniedBy: currentUser?.email || currentUser?.uid || null,
        closeRequestDeniedAt: new Date(),
        closeRequestApproved: false,
        closeRequestApprovedBy: null,
        closeRequestApprovedAt: null,
      });

      setComplaints((prevComplaints) =>
        prevComplaints.map((complaint) =>
          complaint.id === denyCloseRequestComplaintId
            ? {
                ...complaint,
                closeRequestPending: false,
                closeRequestDenied: true,
                closeRequestDeniedReason: trimmedReason,
                closeRequestApproved: false,
                closeRequestApprovedBy: null,
                closeRequestApprovedAt: null,
              }
            : complaint
        )
      );

      handleCloseDenyCloseRequestDialog();
    } catch (err) {
      console.error("Error denying close request:", err);
      setDenyCloseRequestError("Failed to deny request. Please try again.");
    }
  };

  const handleOpenReasonDialog = (reason) => {
    setReasonDialogText(reason || "No reason provided.");
    setReasonDialogOpen(true);
  };

  const handleCloseReasonDialog = () => {
    setReasonDialogOpen(false);
    setReasonDialogText("");
  };

  const rows = complaints.map((complaint) => {
    const item = itemDetails[complaint.itemId] || {};
    return {
      id: complaint.id,
      userEmail:
        complaint.userEmail ||
        (complaint.userId ? auth.currentUser?.email : "N/A"),
      itemId: complaint.itemId,
      issueDescription: complaint.issueDescription || "N/A",
      itemMake: item.itemMake || "N/A",
      model: item.model || "N/A",
      purchaseDate: formatDate(item.purchaseDate),
      warrantyExpiryDate: formatDate(item.warrantyExpiryDate),
      status: normalizeComplaintStatus(complaint.status),
      closeRequestPending: complaint.closeRequestPending === true,
      closeRequestDenied: complaint.closeRequestDenied === true,
      closeRequestDeniedReason: complaint.closeRequestDeniedReason || "",
      closeRequestApproved:
        complaint.closeRequestApproved === true ||
        Boolean(complaint.closeRequestApprovedAt) ||
        Boolean(complaint.closeRequestApprovedBy),
      assignedSellerId: complaint.assignedSellerId || "",
      assignedSellerEmail: complaint.assignedSellerEmail || "",
      assignedSellerName: complaint.assignedSellerName || "",
      facilityEmail: complaint.facilityEmail || item.facilityEmail || "",
      feedbackRating: Number(complaint.feedbackRating || 0),
      feedbackRemarks: complaint.feedbackRemarks || "",
    };
  });

  const filteredRows = rows.filter((row) => {
    const normalizedStatus = normalizeComplaintStatus(row.status);
    if (complaintFilter === "resolved") {
      return normalizedStatus === "Resolved";
    }

    // Default active filter: show only Open and In Progress complaints.
    return normalizedStatus === "Open" || normalizedStatus === "In Progress";
  });

  const showAssignedSellerColumn =
    isFacilityAdmin ||
    complaints.some(
      (complaint) =>
        Boolean(complaint.assignedSellerId) ||
        Boolean(complaint.assignedSellerEmail)
    );

  const showFeedbackColumn = isFacilityAdmin || isSeller;

  const tableColumnCount =
    9 + (showAssignedSellerColumn ? 1 : 0) + (showFeedbackColumn ? 1 : 0);

  return (
    <Box sx={{ flexGrow: 1, p: 3, bgcolor: "grey.100" }}>
      <Container maxWidth="xl">
        <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Raised Complaints List
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                setComplaintFilter((prev) =>
                  prev === "resolved" ? "active" : "resolved"
                )
              }
            >
              {complaintFilter === "resolved" ? "Back" : "Resolved Complaints"}
            </Button>
          </Box>

          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Complaint ID
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Raised By (Email)
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Make
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Model
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Purchase Date
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Warranty Expiry Date
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Issue
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Status
                  </TableCell>
                  {showAssignedSellerColumn && (
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Assigned Facility Operator
                    </TableCell>
                  )}
                  {showFeedbackColumn && (
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      Feedback
                    </TableCell>
                  )}
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={tableColumnCount} align="center" sx={{ p: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColumnCount} align="center" sx={{ p: 5 }}>
                      No complaints found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => (
                    <TableRow hover key={row.id}>
                      {(() => {
                        const complaint = complaints.find((entry) => entry.id === row.id) || {};
                        const editable = canEditStatus(complaint);
                        const statusOptions = getStatusOptionsForComplaint(complaint);
                        const selectedStatus = draftStatuses[row.id] || normalizeComplaintStatus(row.status);
                        const hasUnsavedStatusChange = Boolean(draftStatuses[row.id]);
                        const canViewDeniedCloseRequest =
                          isFacilityAdmin || isSeller || userRole === "admin";

                        return (
                          <>
                      <TableCell align="center">{row.id}</TableCell>
                      <TableCell align="center">{row.userEmail}</TableCell>
                      <TableCell align="center">{row.itemMake}</TableCell>
                      <TableCell align="center">{row.model}</TableCell>
                      <TableCell align="center">{row.purchaseDate}</TableCell>
                      <TableCell align="center">
                        {row.warrantyExpiryDate}
                      </TableCell>
                      <TableCell align="center">
                        {row.issueDescription}
                      </TableCell>
                      <TableCell align="center">
                        {row.closeRequestApproved ? (
                          <Chip
                            label={normalizeComplaintStatus(row.status)}
                            color="success"
                            size="small"
                          />
                        ) : row.closeRequestPending ? (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                            <Chip
                              label="Close Requested"
                              color="warning"
                              size="small"
                            />
                            {isFacilityAdmin && (
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="success"
                                  onClick={() => handleApproveCloseRequest(row.id)}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    minWidth: 96,
                                    borderRadius: 2,
                                    boxShadow: "none",
                                    "&:hover": { boxShadow: "none" },
                                  }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenDenyCloseRequestDialog(row.id)}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    minWidth: 96,
                                    borderRadius: 2,
                                  }}
                                >
                                  Deny
                                </Button>
                              </Box>
                            )}
                          </Box>
                        ) : row.closeRequestDenied && canViewDeniedCloseRequest ? (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                            <Button
                              variant="text"
                              size="small"
                              color="error"
                              onClick={() => handleOpenReasonDialog(row.closeRequestDeniedReason)}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              Close Request Denied
                            </Button>
                            {isSeller && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => openCloseRequestDialog(row.id)}
                              >
                                Close Request
                              </Button>
                            )}
                          </Box>
                        ) : editable ? (
                          <Select
                            value={selectedStatus}
                            onChange={(e) =>
                              handleStatusSelection(row.id, e.target.value)
                            }
                            size="small"
                            sx={{ minWidth: 120 }}
                          >
                            {statusOptions.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Chip
                            label={normalizeComplaintStatus(row.status)}
                            color={getComplaintStatusColor(row.status)}
                            size="small"
                          />
                        )}
                      </TableCell>
                      {showAssignedSellerColumn && (
                        <TableCell align="center">
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                            {row.assignedSellerEmail ? (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {row.assignedSellerName || "Facility Operator"}
                                </Typography>
                                {isFacilityAdmin &&
                                  normalizeComplaintStatus(row.status) === "Open" && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                      openAssignDialog(
                                        row.id,
                                        row.assignedSellerId || ""
                                      )
                                    }
                                  >
                                    Reassign
                                  </Button>
                                )}
                              </>
                            ) : (
                              isFacilityAdmin && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => openAssignDialog(row.id, "")}
                                >
                                  Assign
                                </Button>
                              )
                            )}
                          </Box>
                        </TableCell>
                      )}
                      {showFeedbackColumn && (
                        <TableCell align="center">
                          {row.feedbackRating > 0 ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                              <Rating value={row.feedbackRating} precision={1} readOnly size="small" />
                              {row.feedbackRemarks ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ maxWidth: 180, wordBreak: "break-word" }}
                                >
                                  {row.feedbackRemarks}
                                </Typography>
                              ) : null}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {isCustomer ? (
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenHelpDialog(row.id)}
                            >
                              Help
                            </Button>
                            {normalizeComplaintStatus(row.status) === "Resolved" && (
                              row.feedbackRating > 0 ? (
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  Feedback Submitted
                                </Typography>
                              ) : (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleOpenFeedbackDialog(row.id)}
                                >
                                  User Feedback
                                </Button>
                              )
                            )}
                          </Box>
                        ) : editable || canDeleteComplaint() ? (
                          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
                            {hasUnsavedStatusChange && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveStatus(row.id)}
                              >
                                Save
                              </Button>
                            )}
                            {canDeleteComplaint() && (
                              <Button
                                color="error"
                                onClick={() => handleDelete(row.id)}
                                startIcon={<DeleteIcon />}
                              >
                                Delete
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                          </>
                        );
                      })()}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {["customer", "shop", "admin"].includes(userRole) && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 3 }} // Replaced inline style
              onClick={() => navigate("/warranty_list")}
            >
              Back to Warranty List
            </Button>
          )}
        </Paper>
      </Container>

      <Dialog open={closeRequestDialogOpen} onClose={handleCloseRequestCancel}>
        <DialogTitle>Raise close request</DialogTitle>
        <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1 }}>
          <Button onClick={handleCloseRequestCancel}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleRaiseCloseRequest}>
            Close Complaint
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={denyCloseRequestDialogOpen} onClose={handleCloseDenyCloseRequestDialog} fullWidth maxWidth="sm">
        <DialogTitle>Deny Close Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Please provide the reason for denying this close request.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={denyCloseRequestReason}
            onChange={(event) => {
              setDenyCloseRequestReason(event.target.value);
              if (denyCloseRequestError) {
                setDenyCloseRequestError("");
              }
            }}
          />
          {denyCloseRequestError ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {denyCloseRequestError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDenyCloseRequestDialog}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDenyCloseRequest}>
            Deny Request
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reasonDialogOpen} onClose={handleCloseReasonDialog} fullWidth maxWidth="sm">
        <DialogTitle>Close Request Denied</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
            Reason
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
            {reasonDialogText}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseReasonDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={helpDialogOpen} onClose={handleCloseHelpDialog} fullWidth maxWidth="sm">
        <DialogTitle>Report Issue To Facility Admin</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe your issue below. Your report will be sent to the facility admin email.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Issue Details"
            value={helpMessage}
            onChange={(event) => setHelpMessage(event.target.value)}
            disabled={sendingHelp}
          />
          {helpError ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {helpError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseHelpDialog} disabled={sendingHelp}>Cancel</Button>
          <Button variant="contained" onClick={handleSendHelpReport} disabled={sendingHelp}>
            {sendingHelp ? "Sending..." : "Send Report"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={feedbackDialogOpen} onClose={handleCloseFeedbackDialog} fullWidth maxWidth="sm">
        <DialogTitle>User Feedback</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rate your complaint resolution and share remarks.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Rating
              name="complaint-feedback"
              value={feedbackRating}
              onChange={(_, newValue) => setFeedbackRating(newValue || 0)}
              size="large"
              disabled={savingFeedback}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Remarks"
            value={feedbackRemarks}
            onChange={(event) => setFeedbackRemarks(event.target.value)}
            disabled={savingFeedback}
          />
          {feedbackError ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {feedbackError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseFeedbackDialog} disabled={savingFeedback}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFeedback} disabled={savingFeedback}>
            {savingFeedback ? "Saving..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={closeAssignDialog} fullWidth maxWidth="sm">
        <DialogTitle>Assign Complaint To Facility Operator</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="assign-seller-label">Facility Operator</InputLabel>
            <Select
              labelId="assign-seller-label"
              value={selectedAssignSellerId}
              label="Facility Operator"
              onChange={(event) => setSelectedAssignSellerId(event.target.value)}
            >
              {facilitySellers.map((seller) => (
                <MenuItem key={seller.id} value={seller.id}>
                  {seller.displayName} ({seller.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {facilitySellers.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No facility operators are available for this facility.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAssignDialog} disabled={assigningComplaint}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignComplaint}
            disabled={assigningComplaint || !selectedAssignSellerId || facilitySellers.length === 0}
          >
            {assigningComplaint ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ComplaintsList;